import React, { FC } from 'react';
import { getLayouts } from './__queries__';
import SelectList from '~ComponentsImproved/SelectList';
import { addToast } from '~actions';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import useI18n from '~Translations';
import { useDispatch, useLogger, useSelector } from '~Hooks';
import Loader from '~ComponentsImproved/Loader';
import { SettingsBlock, SettingsLoaderWrapper } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Toggle from '~ComponentsImproved/Toggle';
import { useStorage } from '~Components/Storage';

const GET_LAYOUTS = gql`
  query getLayouts($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id
        layouts {
          id
          name {
            id
            se
            en
            es
            no
          }
        }
      }
    }
  }
`;

const PosLayoutSettings: FC = () => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const logger = useLogger('settings-v2');
  const [storage, updateStorage, storageLoading] = useStorage();

  const venueId = useSelector((state) => state.venueId);
  const POSLayout = useSelector((state) => state.POSLayout);

  const layoutsQuery = useQuery<getLayouts>(GET_LAYOUTS, {
    variables: { venueId },
    skip: !venueId,
  });

  const updatePOSLayout = (layout: string) => {
    dispatch({
      type: 'SET_POS_LAYOUT',
      POSLayout: layout,
    });
    window.postMessageBridge({
      devicePOSLayout: layout,
    });

    logger.info('Updating POS Layout', { fields: { layout } });
    dispatch(addToast(i18n('TOAST.Layout.Updated'), true));
  };

  if (layoutsQuery.loading || storageLoading) {
    return (
      <SettingsLoaderWrapper>
        <Loader />
      </SettingsLoaderWrapper>
    );
  }

  const layouts = layoutsQuery.data?.venue?.company?.layouts ?? [];
  const layoutList = layouts.map((layout) => ({
    id: layout.id,
    name: i18n(layout.name),
  }));

  const togglesConfig: Array<{
    label: string;
    subLabel?: string;
    key: string;
    storage: any;
    // Will reverse the value of the storage
    inverted?: boolean;
  }> = [
    {
      label: i18n('Settings.PosLayout.ShowPrices'),
      key: 'showPricesOnButtons',
      storage: storage.showPricesOnButtons,
    },
    {
      label: i18n('Settings.PosLayout.ShowImages'),
      key: 'showImagesOnButtons',
      storage: storage.showImagesOnButtons,
    },
    {
      label: i18n('Settings.PosLayout.RequireBeeper'),
      subLabel: i18n('Settings.PosLayout.RequireBeeper.Desc'),
      key: 'requireBeeperNumber',
      storage: storage.requireBeeperNumber,
    },
  ];

  return (
    <>
      <Box padding="large">
        <Heading level={4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.PosLayout.Choose')}
        </Heading>
        <SelectList
          dark
          list={[
            {
              id: 'NONE',
              name: i18n('Settings.LayoutNone') + ' ' + i18n('Settings.LayoutUseCategories'),
            },
            ...layoutList,
          ]}
          selectedId={POSLayout ?? 'NONE'}
          onSelect={updatePOSLayout}
        />
      </Box>
      <Box padding="large">
        <Heading level={4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.PosLayout.Settings')}
        </Heading>
        <SettingsBlock>
          {togglesConfig.map((toggle) => (
            <Toggle
              key={toggle.key}
              settingKey={toggle.key}
              label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
              description={toggle.subLabel}
              initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
              onToggle={() => updateStorage({ [toggle.key]: !toggle.storage })}
              fullWidth
              darkBackground
              lightText
            />
          ))}
        </SettingsBlock>
      </Box>
    </>
  );
};

export default PosLayoutSettings;
